import { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { storeService } from '../services/api/store/storeApi';
import AuthContext from '../contexts/AuthContext';

export const useAffiliate = (productId) => {
  const user = useSelector(state => state.user.user);
  const authContext = useContext(AuthContext);
  const [affiliateStatus, setAffiliateStatus] = useState({
    isAffiliate: false,
    status: null,
    commissionRate: null,
    joinedAt: null
  });
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState(null);

  // Buscar userId de múltiplas fontes
  const getUserId = () => {
    // 1. Tentar do AuthContext primeiro
    if (authContext?.getUserId) {
      const contextUserId = authContext.getUserId();
      if (contextUserId) return contextUserId;
    }
    
    // 2. Tentar do Redux store
    if (user?.id) return user.id;
    
    // 3. Tentar do localStorage como backup
    try {
      const userData = JSON.parse(localStorage.getItem('user_data'));
      if (userData?.id) return userData.id;
    } catch (e) {
      console.warn('useAffiliate - Erro ao ler user_data:', e);
    }
    
    // 4. Fallback para ID 12 (Cesar) se não houver usuário logado
    console.warn('useAffiliate - Nenhum userId encontrado, usando fallback ID 12');
    return 12;
  };

  // Verificar status de afiliação
  const checkAffiliateStatus = async () => {
    const userId = getUserId();
    if (!userId || !productId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await storeService.getAffiliateStatus(productId, userId);
      
      if (response.success) {
        setAffiliateStatus({
          isAffiliate: response.is_affiliate,
          status: response.affiliate_status,
          commissionRate: response.commission_rate,
          joinedAt: response.joined_at
        });
      } else {
        throw new Error(response.message || 'Erro ao verificar status de afiliação');
      }
    } catch (err) {
      console.error('Erro ao verificar status de afiliação:', err);
      setError(err.message || 'Erro ao verificar status de afiliação');
    } finally {
      setLoading(false);
    }
  };

  // Promover produto (tornar-se afiliado)
  const promoteProduct = async () => {
    const userId = getUserId();
    if (!userId || !productId) {
      setError('Usuário não autenticado');
      return false;
    }

    setPromoting(true);
    setError(null);

    try {
      const response = await storeService.promoteProduct(productId, userId);
      
      if (response.success) {
        // Atualizar status local
        setAffiliateStatus(prev => ({
          ...prev,
          isAffiliate: true,
          status: response.status,
          commissionRate: response.commission_rate,
          joinedAt: new Date().toISOString()
        }));

        return {
          success: true,
          message: response.message,
          status: response.status,
          autoApproved: response.auto_approved
        };
      } else {
        throw new Error(response.message || 'Erro ao promover produto');
      }
    } catch (err) {
      console.error('Erro ao promover produto:', err);
      const errorMessage = err.message || 'Erro ao promover produto';
      setError(errorMessage);
      
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setPromoting(false);
    }
  };

  // Verificar status quando o componente monta ou productId muda
  useEffect(() => {
    if (productId) {
      checkAffiliateStatus();
    }
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Estado
    affiliateStatus,
    loading,
    promoting,
    error,
    
    // Actions
    checkAffiliateStatus,
    promoteProduct
  };
};

export default useAffiliate;